import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  ArrowRight, Zap, Target, PenTool, BarChart3, Share2,
  TrendingUp, Star, CheckCircle2, Play,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

/* ─── Animations ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

/* ─── Features ─── */
const FEATURES = [
  { icon: Target,     title: "Discover Opportunities",  body: "Uncover what your niche is searching for before anyone else creates it. Real demand signals, not guesswork." },
  { icon: PenTool,    title: "Script Studio",            body: "Turn a validated topic into a fully structured script with hooks, body, and CTA — matched to your voice." },
  { icon: BarChart3,  title: "Creator Analytics",        body: "Track YouTube and Instagram performance in one clean dashboard. Watch hours, engagement, subscriber velocity." },
  { icon: Share2,     title: "Publish Everywhere",       body: "Optimize titles, descriptions, and hashtags per platform and schedule across all channels." },
  { icon: TrendingUp, title: "Learning Loop",            body: "Real performance feeds back into every recommendation so the platform gets smarter the more you use it." },
  { icon: Zap,        title: "Intelligence Engine",      body: "Deep research signals from comment sections, trending discussions, and competitor gaps — surfaced automatically." },
];

const STATS = [
  { value: "10x",   label: "Faster research" },
  { value: "3.4×",  label: "Avg. view growth" },
  { value: "5 min", label: "From idea to script" },
  { value: "50+",   label: "Signal sources" },
];

/* ─── Floating avatar pill (on coral hero) ─── */
function AvatarPill({ src, name, tag, className }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.6 }}
      className={`flex items-center gap-2.5 rounded-full border border-white/20 bg-black/30 px-3 py-2 shadow-2xl backdrop-blur-md ${className}`}
    >
      <img src={src} alt={name} className="h-8 w-8 rounded-full object-cover ring-2 ring-white/30" />
      <div className="leading-none">
        <p className="text-xs font-bold text-white">{name}</p>
        <p className="mt-0.5 text-[10px] text-white/60">{tag}</p>
      </div>
    </motion.div>
  );
}

/* ─── Dynamic centre nav pill ─── */
function NavPill() {
  const { pathname } = useLocation();

  return (
    <div className="hidden items-center gap-1 rounded-full border border-white/20 bg-white/10 px-2 py-1.5 backdrop-blur-md md:flex">
      {NAV_ITEMS.map(({ to, icon: Icon, label, featured }) => {
        const isActive = pathname === to;
        const isHighlighted = featured || isActive;

        return (
          <Link
            key={to}
            to={to}
            aria-label={label}
            title={label}
            className={[
              "relative flex items-center justify-center rounded-full transition-all duration-200",
              isHighlighted
                ? "h-10 w-10 bg-white shadow-lg"
                : "h-9 w-9 text-white/70 hover:bg-white/15 hover:text-white",
            ].join(" ")}
          >
            <Icon
              className={[
                "h-4 w-4 transition-colors",
                isHighlighted ? "text-foreground" : "",
              ].join(" ")}
            />
            {/* Active indicator dot */}
            {isActive && !featured && (
              <span className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-white" />
            )}
          </Link>
        );
      })}
    </div>
  );
}

export default function Landing() {
  usePageTitle(null); // root page — just "Virality"
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If user is already authenticated, redirect straight to dashboard
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!authLoading && user && !params.get("preview")) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, authLoading, navigate, location.search]);

  return (
    <div className="bg-background text-foreground">

      {/* ── NAV (floats over hero) ── */}
      <header className="absolute left-0 right-0 top-0 z-40">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          {/* White logo on coral hero */}
          <Link to="/" className="flex items-center transition-opacity hover:opacity-90">
            <Logo variant="white" size="lg" />
          </Link>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/dashboard">
                <button
                  className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-foreground shadow-lg transition-transform hover:scale-105"
                  data-testid="nav-dashboard"
                >
                  Go to Dashboard
                </button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <button
                    className="hidden rounded-full px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:text-white sm:block"
                    data-testid="nav-login"
                  >
                    Log in
                  </button>
                </Link>
                <Link to="/signup">
                  <button
                    className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-foreground shadow-lg transition-transform hover:scale-105"
                    data-testid="nav-signup"
                  >
                    Get started
                  </button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4 pb-24 pt-24 sm:px-6" data-testid="hero-banner">
        {/* BG — doodle illustration with coral overlay */}
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          {/* Layer 1 — base coral gradient (always visible) */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(155deg, #BA320D 0%, #DE4B23 35%, #F06A3F 75%, #D4421E 100%)" }} />

          {/* Layer 2 — doodle illustration scaled to fill, low opacity so coral shows through */}
          <img
            src="/hero-illustration.jpg"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover object-center select-none"
            style={{ opacity: 0.12, mixBlendMode: "luminosity" }}
            loading="eager"
          />

          {/* Layer 3 — coral wash on top to unify and ensure text contrast */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(155deg, rgba(186,50,13,0.72) 0%, rgba(222,75,35,0.65) 40%, rgba(240,106,63,0.60) 100%)" }} />

          {/* Layer 4 — subtle radial glow for depth */}
          <div className="absolute right-1/4 top-1/4 h-[500px] w-[500px] rounded-full opacity-20 blur-[120px]" style={{ background: "radial-gradient(circle, #FFA260, transparent 70%)" }} />

          {/* Layer 5 — fade to page bg at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background to-transparent" />
        </div>

        {/* Floating creator social proof pills */}
        <AvatarPill src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&auto=format" name="Amara K." tag="1.4M subscribers" className="animate-float absolute left-[4%] top-[18%] hidden xl:flex z-20" />
        <AvatarPill src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&auto=format" name="James L." tag="Tech Creator" className="animate-float-delayed absolute left-[5%] bottom-[22%] hidden xl:flex z-20" />
        <AvatarPill src="https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=80&h=80&fit=crop&auto=format" name="Sofia R." tag="892K followers" className="animate-float absolute right-[4%] top-[20%] hidden xl:flex z-20" />
        <AvatarPill src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&auto=format" name="Marcus T." tag="Lifestyle · 3.2M" className="animate-float-delayed absolute right-[5%] bottom-[20%] hidden xl:flex z-20" />

        {/* Hero Content */}
        <div className="relative z-10 mx-auto max-w-4xl px-4 py-8 text-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-1.5 shadow-sm backdrop-blur-md">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
            <span className="text-xs font-semibold uppercase tracking-widest text-white">Virality 2.0 — AI Creator Platform</span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white xs:text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
            The All-in-one<br />
            <span className="text-white/95">Creator Platform.</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/90 sm:text-lg">
            Market research, content strategy, script writing, and performance analytics — one connected workflow so you always know what to create next.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to={user ? "/dashboard" : "/signup"}>
              <button className="group flex items-center gap-2 rounded-full bg-white px-8 py-3.5 font-display text-sm font-bold text-foreground shadow-2xl transition-all hover:scale-105 hover:bg-white/95" data-testid="cta-start">
                {user ? "Open Workspace" : "Get started free"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </Link>
            <Link to="/waitlist">
              <button className="flex items-center gap-2 rounded-full border border-white/40 bg-white/15 px-8 py-3.5 font-display text-sm font-semibold text-white backdrop-blur-md transition-all hover:bg-white/25 shadow-lg" data-testid="cta-waitlist">
                <Play className="h-3.5 w-3.5 fill-white" /> See how it works
              </button>
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs sm:text-sm text-white/85">
            {["No credit card required", "Free early access", "Setup in 2 minutes"].map((t) => (
              <div key={t} className="flex items-center gap-1.5 font-medium">
                <CheckCircle2 className="h-4 w-4 text-white" /> {t}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="border-b border-border bg-surface py-8">
        <div className="mx-auto max-w-5xl px-6">
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {STATS.map((s, i) => (
              <motion.div key={s.label} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp} className="text-center">
                <p className="font-display text-4xl font-extrabold text-accent">{s.value}</p>
                <p className="mt-1 text-xs text-muted-foreground">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURE 1 — Intelligence Engine ── */}
      <section className="px-4 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-6xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55 }}
            className="overflow-hidden rounded-3xl border border-border bg-surface shadow-sm">
            <div className="grid items-center gap-8 p-8 md:grid-cols-2 md:p-12">
              {/* Text */}
              <div>
                <span className="inline-block rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent shadow-sm">
                  Intelligence Engine
                </span>
                <h2 className="mt-5 font-display text-4xl font-extrabold leading-tight tracking-tight text-foreground md:text-5xl">
                  KNOW WHAT<br />TO CREATE
                </h2>
                <p className="mt-4 text-sm uppercase tracking-widest text-muted-foreground">Discover topics before competitors do</p>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
                  Real research signals from comment sections, trending discussions, and content gaps — surfaced in seconds so you never miss an opportunity.
                </p>
                <Link to="/signup" className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-2.5 text-sm font-semibold text-white hover:bg-foreground/80">
                  Explore Intelligence <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              {/* Mock card */}
              <div className="rounded-2xl border border-border bg-background p-5 shadow-xl">
                <div className="mb-3 flex items-center justify-center overflow-hidden rounded-xl border border-border/80 bg-white p-2 shadow-sm">
                  <img src="/assets/radar-intelligence.png" alt="Topic Opportunity Radar Chart" className="h-48 w-full object-contain" />
                </div>
                {/* Validated Opportunity Intelligence Card */}
                <div className="rounded-xl border border-border bg-elevated/50 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-accent" />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-accent">Top Detected Opportunity</span>
                    </div>
                    <span className="rounded-full bg-accent/10 px-2.5 py-0.5 text-xs font-extrabold text-accent">
                      94 Score
                    </span>
                  </div>

                  <h4 className="mt-2.5 font-display text-sm font-bold text-foreground leading-snug">
                    AI’s Impact on Finance: The Workflows Behind Modern Trading
                  </h4>


                  <div className="mt-3.5 grid grid-cols-3 gap-2 border-t border-border/80 pt-3">
                    <div className="text-center">
                      <p className="font-display text-base font-extrabold text-foreground">94%</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Demand</p>
                    </div>
                    <div className="border-x border-border/80 text-center">
                      <p className="font-display text-base font-extrabold text-foreground">98%</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Confidence</p>
                    </div>
                    <div className="text-center">
                      <p className="font-display text-base font-extrabold text-green-600">Low</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Competition</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FEATURE GRID ── */}
      <section className="px-4 pb-20 md:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">The full workflow</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              Everything you need in one platform
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-30px" }} custom={i} variants={fadeUp}
                  className="card-hover rounded-2xl border border-border bg-surface p-6 shadow-sm" data-testid={`feature-${i}`}>
                  <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-display text-base font-bold text-foreground">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FEATURE 2 — Script Studio ── */}
      <section className="px-4 pb-20 md:px-8">
        <div className="mx-auto max-w-6xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55 }}
            className="overflow-hidden rounded-3xl border border-border bg-surface shadow-sm">
            <div className="grid items-center gap-8 p-8 md:grid-cols-2 md:p-12">
              {/* Mock editor */}
              <div className="order-2 rounded-2xl border border-border bg-background p-5 shadow-xl md:order-1">
                <div className="mb-3 flex items-center justify-between border-b border-border pb-3">
                  <span className="text-xs font-semibold text-foreground">Script Studio</span>
                  <span className="rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700">Est. 2m 45s</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-accent">[HOOK — 0:00]</p>
                    <div className="rounded-xl border border-border bg-elevated p-3 text-xs text-foreground leading-relaxed">
                      "Most people think AI in finance is just ChatGPT for stock tips — but hedge funds are using something completely different."
                    </div>
                  </div>
                  <div>
                    <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">[BODY — 0:20]</p>
                    <div className="rounded-xl border border-border bg-elevated p-3 text-xs text-foreground leading-relaxed">
                      "Here is the exact 3-step workflow modern analysts use to find undervalued assets in minutes…"
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                    <span className="text-xs text-muted-foreground">Hook score: <strong className="text-foreground">94 / 100</strong></span>
                  </div>
                </div>
              </div>
              {/* Text */}
              <div className="order-1 md:order-2">
                <span className="inline-block rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-semibold text-green-700 shadow-sm">
                  Script Studio
                </span>
                <h2 className="mt-5 font-display text-4xl font-extrabold leading-tight tracking-tight text-foreground md:text-5xl">
                  FROM IDEA<br />TO SCRIPT
                </h2>
                <p className="mt-4 text-sm uppercase tracking-widest text-muted-foreground">Write faster with AI-matched voice</p>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
                  Turn any validated topic into a full script — hooks, body, CTA — matched to your speaking style. Includes hook scoring so you always start with the best angle.
                </p>
                <Link to="/signup" className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-2.5 text-sm font-semibold text-white hover:bg-foreground/80">
                  Try Script Studio <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="px-4 pb-24 md:px-8">
        <div className="mx-auto max-w-6xl">
          <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-3xl">
            <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1400&h=500&fit=crop&auto=format&q=80"
              alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(13,13,13,0.93) 0%, rgba(40,20,10,0.90) 100%)" }} />
            <div className="relative z-10 px-8 py-20 text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent">Ready when you are</p>
              <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-extrabold text-white md:text-5xl">
                Stop guessing. Start creating content that actually performs.
              </h2>
              <p className="mx-auto mt-4 max-w-lg text-sm text-white/50">
                Join creators who've replaced spreadsheets, guesswork, and multiple tools with one intelligent platform.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <Link to={user ? "/dashboard" : "/signup"}>
                  <button className="rounded-full bg-accent px-8 py-3.5 font-display text-sm font-bold text-white shadow-lg transition-transform hover:scale-105 hover:bg-accent-hover" data-testid="cta-bottom">
                    {user ? "Open Workspace" : "Start Creating Free"} <ArrowRight className="ml-1 inline h-4 w-4" />
                  </button>
                </Link>
                <Link to="/waitlist">
                  <button className="rounded-full border border-white/20 px-8 py-3.5 font-display text-sm font-semibold text-white/70 transition-colors hover:border-white/40 hover:text-white">
                    Join Waitlist
                  </button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-border bg-surface">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 py-8 text-sm text-muted-foreground sm:flex-row">
          <Link to="/" className="flex items-center transition-opacity hover:opacity-90">
            <Logo variant="dark" />
          </Link>
          <p className="text-xs">© {new Date().getFullYear()} Virality — Content Intelligence OS</p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
            <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/cookies" className="hover:text-foreground">Cookies</Link>
            <Link to="/waitlist" className="hover:text-foreground">Waitlist</Link>
            {user ? (
              <Link to="/dashboard" className="hover:text-foreground font-semibold text-accent">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="hover:text-foreground">Log in</Link>
                <Link to="/signup" className="hover:text-foreground font-semibold text-accent">Sign up</Link>
              </>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
